import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { emailQuoteShare } from "../../lib/api";

// Inline form embedded inside a ShareLinksModal row — fires the
// per-share Resend email and pushes the new emailed_to entry back via
// onSent so the parent re-renders the history list.
export function EmailShareForm({ share, defaultEmail, defaultName, onClose, onSent }) {
    const [toEmail, setToEmail] = useState(defaultEmail || "");
    const [recipientName, setRecipientName] = useState(defaultName || "");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    const submit = async () => {
        const trimmed = (toEmail || "").trim();
        if (!trimmed.includes("@")) {
            toast.error("Enter a valid email address.");
            return;
        }
        setSending(true);
        try {
            const resp = await emailQuoteShare(share.id, {
                to_email: trimmed,
                recipient_name: recipientName.trim(),
                custom_message: message,
            });
            toast.success(`Email sent to ${trimmed}`);
            onSent?.({
                to_email: trimmed,
                recipient_name: recipientName.trim(),
                sent_at: new Date().toISOString(),
                message_id: resp?.message_id || "",
            });
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't send email",
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="mt-2 p-2.5 rounded-sm bg-[#0a0a0a] border border-white/15 space-y-2"
            data-testid={`quote-share-email-form-${share.id}`}
        >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">
                        Customer email
                    </label>
                    <input
                        type="email"
                        value={toEmail}
                        onChange={(e) => setToEmail(e.target.value)}
                        placeholder="customer@example.com"
                        autoFocus
                        data-testid={`quote-share-email-to-${share.id}`}
                        className="w-full px-2 py-1.5 text-xs bg-[#111] border border-white/15 rounded-sm text-white placeholder:text-slate-500 focus:border-[#1a3fd1]/50 outline-none"
                    />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">
                        Recipient name <span className="font-normal lowercase text-slate-600">(optional)</span>
                    </label>
                    <input
                        type="text"
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder="Jane"
                        data-testid={`quote-share-email-name-${share.id}`}
                        className="w-full px-2 py-1.5 text-xs bg-[#111] border border-white/15 rounded-sm text-white placeholder:text-slate-500 focus:border-[#1a3fd1]/50 outline-none"
                    />
                </div>
            </div>
            <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">
                    Personal note <span className="font-normal lowercase text-slate-600">(optional)</span>
                </label>
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={2}
                    maxLength={1000}
                    placeholder="Hi Jane, here's the quote we discussed — let me know if anything looks off."
                    data-testid={`quote-share-email-message-${share.id}`}
                    className="w-full px-2 py-1.5 text-xs bg-[#111] border border-white/15 rounded-sm text-white placeholder:text-slate-500 focus:border-[#1a3fd1]/50 outline-none resize-y"
                />
            </div>
            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-2 py-1.5 text-[11px] rounded-sm text-slate-300 hover:text-white"
                    data-testid={`quote-share-email-cancel-${share.id}`}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={submit}
                    disabled={sending}
                    data-testid={`quote-share-email-send-${share.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] rounded-sm bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white font-bold disabled:opacity-50"
                >
                    {sending ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                        <Send className="w-3 h-3" />
                    )}
                    {sending ? "Sending…" : "Send email"}
                </button>
            </div>
        </div>
    );
}
